import type { Span, TextError } from './source.js';

/**
 * The JavaScript lexer. It is the C++ one (`lex.ts`) with the pieces JavaScript needs and the
 * ones it does not: `===`/`!==`, single quotes and template literals arrive; `<<`, `>>` and
 * `#include` lines leave, so a stray shift operator is reported instead of quietly parsed.
 */
export type JsTokenKind = 'ident' | 'number' | 'string' | 'template' | 'op' | 'eof';

/**
 * One literal piece of a template literal and the `${…}` that follows it. The substitution is
 * kept as a range in the original source rather than as tokens: the parser lexes it on demand,
 * so every token inside it still carries its true line, column and offset.
 */
export interface TemplateChunk {
  /** The literal text before the substitution, already unescaped. */
  text: string;
  /** The `${…}` that follows, without the braces; null on the last chunk. */
  expr: Span | null;
}

export interface JsToken {
  kind: JsTokenKind;
  /** The text as written; for `string` the decoded value, for `template` the raw source. */
  text: string;
  line: number;
  column: number;
  from: number;
  to: number;
  /** Only on a `template` token. */
  chunks?: TemplateChunk[];
}

/** Thrown inside the lexer and turned into a `TextError` at the top of `parseJs`. */
export class JsLexError extends Error {
  constructor(readonly info: TextError) {
    super(info.code);
  }
}

/** Longest first: `===` must win over `==`, which must win over `=`. */
const OPS = [
  '===',
  '!==',
  '<=',
  '>=',
  '==',
  '!=',
  '&&',
  '||',
  '++',
  '--',
  '+=',
  '-=',
  '*=',
  '/=',
  '%=',
  '=>',
  '+',
  '-',
  '*',
  '/',
  '%',
  '<',
  '>',
  '=',
  '!',
  '(',
  ')',
  '{',
  '}',
  '[',
  ']',
  ',',
  ';',
  '.',
  '?',
  ':',
];

const ID_START = /[A-Za-z_$]/;
const ID_PART = /[A-Za-z0-9_$]/;
const DIGIT = /[0-9]/;

function fail(
  code: TextError['code'],
  span: Span,
  line: number,
  column: number,
  params?: Record<string, string>,
): never {
  throw new JsLexError({ code, line, column, span, ...(params ? { params } : {}) });
}

/** The escape sequences a student actually types; anything else is the character itself. */
function unescape(esc: string | undefined): string {
  switch (esc) {
    case 'n':
      return '\n';
    case 't':
      return '\t';
    case 'r':
      return '\r';
    case '\\':
      return '\\';
    default:
      return esc ?? '';
  }
}

/**
 * Turns JavaScript text into tokens. `from`/`to` lex only a slice — that is how a `${…}` inside
 * a template literal is read — while line and column keep counting from the start of `source`,
 * so an error inside a template still points at the right place in the file.
 */
export function lexJs(source: string, from = 0, to = source.length): JsToken[] {
  const tokens: JsToken[] = [];
  let i = from;
  let line = 1;
  let lineStart = 0;
  for (let j = 0; j < from; j++) {
    if (source[j] === '\n') {
      line++;
      lineStart = j + 1;
    }
  }
  const col = () => i - lineStart + 1;

  const newline = () => {
    line++;
    lineStart = i + 1;
  };

  /** Skip a nested quote or backtick inside a `${…}`, so its braces are not miscounted. */
  const skipQuoted = (quote: string) => {
    i++;
    while (i < to && source[i] !== quote) {
      if (source[i] === '\\') {
        i += 2;
        continue;
      }
      if (source[i] === '\n') newline();
      i++;
    }
    i++;
  };

  while (i < to) {
    const c = source[i]!;
    if (c === '\n') {
      newline();
      i++;
      continue;
    }
    if (c === ' ' || c === '\t' || c === '\r') {
      i++;
      continue;
    }
    if (c === '/' && source[i + 1] === '/') {
      while (i < to && source[i] !== '\n') i++;
      continue;
    }
    if (c === '/' && source[i + 1] === '*') {
      const startLine = line;
      const startCol = col();
      const start = i;
      i += 2;
      while (i < to && !(source[i] === '*' && source[i + 1] === '/')) {
        if (source[i] === '\n') newline();
        i++;
      }
      if (i >= to)
        fail('unterminated', { from: start, to }, startLine, startCol, { what: 'comment' });
      i += 2;
      continue;
    }

    const start = i;
    const startCol = col();

    if (DIGIT.test(c)) {
      while (i < to && DIGIT.test(source[i]!)) i++;
      if (source[i] === '.' && DIGIT.test(source[i + 1] ?? '')) {
        i++;
        while (i < to && DIGIT.test(source[i]!)) i++;
      }
      tokens.push({
        kind: 'number',
        text: source.slice(start, i),
        line,
        column: startCol,
        from: start,
        to: i,
      });
      continue;
    }

    if (ID_START.test(c)) {
      while (i < to && ID_PART.test(source[i]!)) i++;
      tokens.push({
        kind: 'ident',
        text: source.slice(start, i),
        line,
        column: startCol,
        from: start,
        to: i,
      });
      continue;
    }

    if (c === '"' || c === "'") {
      i++;
      let value = '';
      while (i < to && source[i] !== c) {
        // A plain string never spans lines; saying so here beats "unexpected }" ten lines later.
        if (source[i] === '\n')
          fail('unterminated', { from: start, to: i }, line, startCol, { what: 'text' });
        if (source[i] === '\\') {
          const esc = source[i + 1];
          value += esc === c ? c : unescape(esc);
          i += 2;
          continue;
        }
        value += source[i];
        i++;
      }
      if (i >= to) fail('unterminated', { from: start, to: i }, line, startCol, { what: 'text' });
      i++;
      tokens.push({ kind: 'string', text: value, line, column: startCol, from: start, to: i });
      continue;
    }

    if (c === '`') {
      const startLine = line;
      i++;
      const chunks: TemplateChunk[] = [];
      let text = '';
      let closed = false;
      while (i < to) {
        const ch = source[i]!;
        if (ch === '`') {
          i++;
          closed = true;
          break;
        }
        if (ch === '\\') {
          const esc = source[i + 1];
          text += esc === '`' || esc === '$' ? esc : unescape(esc);
          if (esc === '\n') newline();
          i += 2;
          continue;
        }
        if (ch === '$' && source[i + 1] === '{') {
          i += 2;
          const exprFrom = i;
          let depth = 1;
          while (i < to) {
            const d = source[i]!;
            if (d === '"' || d === "'" || d === '`') {
              skipQuoted(d);
              continue;
            }
            if (d === '\n') newline();
            if (d === '{') depth++;
            if (d === '}') {
              depth--;
              if (depth === 0) break;
            }
            i++;
          }
          if (i >= to) {
            fail('unterminated', { from: start, to }, startLine, startCol, { what: 'text' });
          }
          chunks.push({ text, expr: { from: exprFrom, to: i } });
          text = '';
          i++;
          continue;
        }
        if (ch === '\n') newline();
        text += ch;
        i++;
      }
      if (!closed)
        fail('unterminated', { from: start, to: i }, startLine, startCol, { what: 'text' });
      chunks.push({ text, expr: null });
      tokens.push({
        kind: 'template',
        text: source.slice(start, i),
        line: startLine,
        column: startCol,
        from: start,
        to: i,
        chunks,
      });
      continue;
    }

    const op = OPS.find((o) => source.startsWith(o, i) && i + o.length <= to);
    if (!op) {
      i++;
      fail('expected', { from: start, to: i }, line, startCol, { found: c });
    }
    i += op.length;
    tokens.push({ kind: 'op', text: op, line, column: startCol, from: start, to: i });
  }

  tokens.push({ kind: 'eof', text: '', line, column: col(), from: to, to });
  return tokens;
}
